import React from "react";
import "./LoanAgainst.css";
import { Helmet } from "react-helmet-async";

const title = "Loan Against Gold";
const pageTitle =
  "Loan Against Gold in Nigeria - Interest Rates, Eligibility & How It Works";
const metaDescription =
  "Need fast cash without selling your gold? Get up to 50% of your gold's value as a loan with same-day approval. Learn how loan against gold works in Nigeria, the interest rates, requirements, and the step-by-step process.";

const LoanAgainstGold = () => {
  return (
    <section className="loan-against-container">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Loan Against Gold",
            provider: {
              "@type": "Organization",
              name: "Respected Solution",
            },
            areaServed: ["Lagos", "Abuja", "Ughelli", "NG"],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Loan Against Gold",
            url: "https://www.respectedsolution.co/loan-against-gold",
          })}
        </script>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
      </Helmet>

      <div
        className="loan-against-hero-slide"
        style={{ backgroundImage: `url(/assets/images/rs/gold_bars.jpg)` }}
      >
        <div className="loan-against-hero-overlay">
          <div className="loan-against-hero-content">
            <h1>{title}</h1>
            <p>Fast, Safe & Cash-Ready in Nigeria</p>
          </div>
        </div>
      </div>

      <div className="loan-against-inner">
        <div className="loan-against-inner-content">
          <section className="loan-intro">
            <p>
              A <strong>loan against gold</strong> lets you unlock instant cash
              without selling your gold. At Respected Solutions, you{" "}
              <strong>
                deposit your gold as collateral, get funded the same day
              </strong>
              , and collect it back once you repay. It works like a pawn loan,
              but with higher security, better valuation, and transparent terms.
            </p>
            <p>
              Whether you own{" "}
              <strong>gold bars, bullion, coins, or jewelry</strong>, you can
              access a quick loan without credit checks, salary slips, or long
              approval processes. Your gold stays safely stored, insured, and
              returned to you once repayment is complete.
            </p>
          </section>

          <section className="loan-against-definition">
            <h2>How Much Can You Borrow?</h2>
            <p>
              You can get{" "}
              <strong>
                up to 50% of the current market value of your gold
              </strong>
              .
            </p>
            <ul>
              <li>The purer the gold, the higher the loan value</li>
              <li>
                The loan amount is paid out{" "}
                <strong>immediately after the valuation</strong>
              </li>
              <li>No hidden fees, no forced selling, no long waiting period</li>
            </ul>
            <p>
              <strong>Example:</strong>
              <br />
              If your gold is valued at <strong>₦2,000,000</strong>, you qualify
              for <strong>up to ₦1,000,000</strong> as a loan.
            </p>
          </section>

          <section className="loan-against-eligibility">
            <h2>
              What We <span>Accept as Collateral</span>
            </h2>
            <ul>
              <li>Gold bars & bullion</li>
              <li>Gold coins</li>
              <li>9K - 24K gold pieces</li>
              <li>White, yellow, and rose gold</li>
              <li>Verified investment-grade gold</li>
              <li>Gold jewelry: rings, chains, bracelets, etc.</li>
              <li>
                Bussdown gold chains & bracelets with diamonds (if gold content
                is verifiable)
              </li>
            </ul>
            <p>
              If it's <strong>real, testable, and legally owned</strong>, it
              qualifies.
            </p>
          </section>

          <section className="loan-against-application-steps">
            <h2>Why Choose This Loan Option</h2>
            <ul>
              <li>
                <strong>Same-day approval & cash disbursement</strong>
              </li>
              <li>
                <strong>
                  Fair interest rate based on loan duration & value
                </strong>
              </li>
              <li>
                <strong>No need to sell your gold or lose ownership</strong>
              </li>
              <li>
                <strong>No credit score or income proof required</strong>
              </li>
              <li>
                <strong>
                  Flexible repayment - collect your gold anytime after
                  settlement
                </strong>
              </li>
              <li>
                <strong>
                  Secure, insured storage for your gold throughout the loan
                </strong>
              </li>
            </ul>
            <p>You get the money you need without sacrificing your assets.</p>
          </section>

          <section className="loan-against-definition">
            <h2>How It Works (Step-By-Step)</h2>
            <ol>
              <li>Bring your gold for testing and valuation</li>
              <li>We confirm purity and market value on the spot</li>
              <li>You deposit the gold as collateral</li>
              <li>
                The loan is transferred to you{" "}
                <strong>within 1 hour or less</strong>
              </li>
              <li>Repay anytime within the agreed period</li>
              <li>
                Collect your gold back in full once repayment is completed
              </li>
            </ol>
            <p>
              If you've ever searched{" "}
              <strong>"loan against gold, how does it work?"</strong>, this is
              the simplest formula:
              <br />
              <strong>
                Deposit gold → Get cash → Repay → Take back your gold.
              </strong>
            </p>
          </section>

          <section className="loan-against-eligibility">
            <h2>
              Eligibility <span>Requirements</span>
            </h2>
            <ul>
              <li>Must be 18+</li>
              <li>Must be the legal owner of the gold</li>
              <li>Gold must be 9K-24K purity</li>
              <li>Valid ID required</li>
              <li>No BVN, payslip, or credit history required</li>
            </ul>
          </section>

          <section className="loan-against-faq">
            <h2>Frequently Asked Questions</h2>
            <div className="loan-against-faq-item">
              <h3>Q: What does "loan against gold" mean?</h3>
              <p>
                It means using your gold as collateral to borrow money without
                selling it.
              </p>
            </div>
            <div className="loan-against-faq-item">
              <h3>Q: Is this the same as pawning?</h3>
              <p>
                Similar, but more secure. Your gold is stored, insured, and
                professionally handled, not risked in a standard pawnshop.
              </p>
            </div>
            <div className="loan-against-faq-item">
              <h3>Q: What is the loan on gold interest rate?</h3>
              <p>
                Rates depend on duration and amount. You can use a{" "}
                <strong>loan on gold interest rate calculator</strong> to
                estimate the total repayment.
              </p>
            </div>
            <div className="loan-against-faq-item">
              <h3>Q: Can I repay early?</h3>
              <p>
                Yes. Early settlement is allowed with{" "}
                <strong>no penalty</strong>.
              </p>
            </div>
            <div className="loan-against-faq-item">
              <h3>Q: What if I fail to repay?</h3>
              <p>The gold may be foreclosed after the agreed grace period.</p>
            </div>
          </section>

          <section className="loan-against-definition">
            <h2>Ready to Get Started?</h2>
            <p>
              <strong>
                Bring your gold, get tested, get paid - all in one visit.
              </strong>
              <br />
              Your gold stays yours. You're only borrowing against it.
            </p>
          </section>
        </div>
      </div>
    </section>
  );
};

export default LoanAgainstGold;
